/**
 * Role / ownership helpers for ADMIN & MERCHANT access control (frontend defense-in-depth).
 * Real security must still be enforced on the backend (see backend-api/TODO.md).
 */
import * as api from '../../modules/api';

export function getCurrentRole() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('role') || '' : '';
  } catch {
    return '';
  }
}

export function getCurrentEmail() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('Email') || '' : '';
  } catch {
    return '';
  }
}

export function isSuperAdmin(role = getCurrentRole()) {
  return role === 'SUPER_ADMIN';
}

export function isAdmin(role = getCurrentRole()) {
  return role === 'ADMIN';
}

export function isMerchant(role = getCurrentRole()) {
  return role === 'MERCHANT';
}

export function isOperator(role = getCurrentRole()) {
  return role === 'OPERATOR';
}

export function isTechnician(role = getCurrentRole()) {
  return role === 'TECHNICIAN';
}

export function canAccessServiceTickets(role = getCurrentRole()) {
  return ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'TECHNICIAN'].includes(role);
}

export function canCreateServiceTicket(role = getCurrentRole()) {
  return role === 'SUPER_ADMIN' || role === 'OPERATOR';
}

export function canAccessServiceQa(role = getCurrentRole()) {
  return role === 'SUPER_ADMIN' || role === 'OPERATOR';
}

export function canAccessServiceTechnicians(role = getCurrentRole()) {
  return ['SUPER_ADMIN', 'ADMIN', 'OPERATOR'].includes(role);
}

/** Sections that only SUPER_ADMIN may open (menu + cross-links + routes). */
export function canAccessUsersSection(role = getCurrentRole()) {
  return isSuperAdmin(role) || isOperator(role);
}

export function canAccessTariffsSection(role = getCurrentRole()) {
  return isSuperAdmin(role);
}

export function canAccessDesignSystem(role = getCurrentRole()) {
  return isSuperAdmin(role);
}

/**
 * Normalize station owner email from various API shapes.
 */
export function getStationOwnerEmail(station) {
  if (!station || typeof station !== 'object') return '';
  const owner =
    station.owner ??
    station.ownerEmail ??
    station.station?.owner ??
    station.station?.ownerEmail ??
    '';
  return String(owner || '').trim().toLowerCase();
}

/**
 * Whether current user may view/edit this station object.
 * SUPER_ADMIN: always. MERCHANT: owner === self. ADMIN: owner in controlled merchants (or self).
 */
export function canAccessStation(station, { role = getCurrentRole(), email = getCurrentEmail(), merchantEmails = [] } = {}) {
  if (isSuperAdmin(role) || isOperator(role)) return true;
  const owner = getStationOwnerEmail(station);
  if (!owner) return false;
  const self = String(email || '').trim().toLowerCase();
  if (isMerchant(role)) return Boolean(self) && owner === self;
  if (isAdmin(role)) {
    if (owner === self) return true;
    const allowed = new Set(
      (Array.isArray(merchantEmails) ? merchantEmails : [])
        .map((e) => String(e || '').trim().toLowerCase())
        .filter(Boolean),
    );
    allowed.add(self);
    return allowed.has(owner);
  }
  return false;
}

/**
 * Load emails of merchants controlled by the current ADMIN (cached per call site).
 * Returns [] for non-ADMIN.
 */
export async function loadAdminMerchantEmails(adminEmail = getCurrentEmail()) {
  if (!isAdmin() || !adminEmail) return [];
  try {
    const { data } = await api.getMerchantsBy(adminEmail);
    const list = Array.isArray(data) ? data : (data?.merchants || data?.content || []);
    return list
      .map((m) => (typeof m === 'string' ? m : m?.email))
      .filter(Boolean)
      .map((e) => String(e).trim().toLowerCase());
  } catch {
    return [];
  }
}

/**
 * Powerbank is in network if its nested station owner is accessible.
 * If station/owner is missing, trust backend list/get (already role-scoped for PB).
 */
export function canAccessPowerbank(powerbank, opts = {}) {
  if (isSuperAdmin(opts.role)) return true;
  const station = powerbank?.station || powerbank?.lastStation || null;
  if (!station) return true;
  const owner = getStationOwnerEmail(station);
  if (!owner) return true;
  return canAccessStation(station, opts);
}

/** Block/unblock powerbank: SUPER_ADMIN + ADMIN only (MERCHANT denied on BE). */
export function canBlockPowerbank(role = getCurrentRole()) {
  return isSuperAdmin(role) || isAdmin(role);
}
